import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Offcanvas } from "react-bootstrap";
import SimpleBar from "simplebar-react";
import SuccessModal from "../../common/successModal";
import arrayUtilities from "../../../utilities/arrayUtilities";
import dateUtilities from "../../../utilities/dateUtilities";
import stringUtilities from "../../../utilities/stringUtilities";
import useApi from "../../../utilities/useApi";
import TelusReportLimitDetails from "./telusReportLimitDetails";

export default function DownloadTelusReport(props) {
    const { api } = useApi();
    const limits = props.sketch?.limits ?? [];

    const [selectedLimitIndices, setSelectedLimitIndices] = useState(limits.map((_, limitIndex) => limitIndex));
    const [language, setLanguage] = useState(localStorage.getItem('telusReportLanguage') ?? 'en');
    const [requestNumber, setRequestNumber] = useState('');
    const [allLimitDetails, setAllLimitDetails] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [previousReports, setPreviousReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');

    useEffect(() => {
        setSelectedLimitIndices(limits.map((_, limitIndex) => limitIndex));
        setAllLimitDetails([]);
    }, [props.sketch?.id]);

    useEffect(() => {
        api.searchTelusReports(props.organisationId, props.sketchId, setPreviousReports, () => { });
    }, []);

    useEffect(() => {
        localStorage.setItem('telusReportLanguage', language);
    }, [language]);

    const isLimitSelected = (limitIndex) => selectedLimitIndices.includes(limitIndex);

    const toggleLimit = (limitIndex) => {
        setSelectedLimitIndices(arrayUtilities.toggle(limitIndex, selectedLimitIndices, !isLimitSelected(limitIndex)));
    };

    const hasCompleteLimitData = (limitDetails, requiredLimitIndices) =>
        requiredLimitIndices.every(limitIndex => {
            if (limitIndex >= limits.length) {
                return false;
            }

            const reportLimitDetail = limitDetails.find(detail => detail.limitIndex === limitIndex);
            return reportLimitDetail !== undefined
                && !stringUtilities.isNullOrEmpty(reportLimitDetail.fromAddress)
                && !stringUtilities.isNullOrEmpty(reportLimitDetail.toAddress)
                && !stringUtilities.isNullOrEmpty(String(reportLimitDetail.serviceCount ?? ''));
        });

    const canSubmit = !stringUtilities.isNullOrEmpty(requestNumber)
        && !arrayUtilities.isNullOrEmpty(selectedLimitIndices)
        && hasCompleteLimitData(allLimitDetails, selectedLimitIndices);

    const onSelectPreviousReport = (telusReportTaskId) => {
        setSelectedReportId(telusReportTaskId);

        if (!telusReportTaskId)
        {
            setLanguage('en');
            setRequestNumber('');
            setAllLimitDetails([]);
            setSelectedLimitIndices(limits.map((_, limitIndex) => limitIndex));
            return;
        }

        const selectedReport = previousReports.find(report => report.telusReportTaskId === telusReportTaskId);
        if (!selectedReport)
        {
            return;
        }

        setLanguage(selectedReport.language);
        setRequestNumber(selectedReport.requestNumber ?? '');
        setAllLimitDetails(selectedReport.limitDetails ?? []);

        const validSelectedLimitIndices = (selectedReport.selections ?? []).reduce((indices, selection) => {
            if (selection.limitIndex < limits.length) {
                indices.push(selection.limitIndex);
            }
            return indices;
        }, []);

        if (validSelectedLimitIndices.length)
        {
            setSelectedLimitIndices(validSelectedLimitIndices);
        }
    };

    const onSubmit = () => {
        if (!canSubmit)
        {
            return;
        }

        setIsSubmitting(true);
        const selectedLimitDetails = allLimitDetails.filter(reportLimitDetail => isLimitSelected(reportLimitDetail.limitIndex));
        const selections = selectedLimitDetails.map(reportLimitDetail => ({ limitIndex: reportLimitDetail.limitIndex, workflowIds: null }));
        const submittedLimitDetails = selectedLimitDetails.map(reportLimitDetail => ({
            limitIndex: reportLimitDetail.limitIndex,
            fromAddress: reportLimitDetail.fromAddress,
            toAddress: reportLimitDetail.toAddress,
            serviceCount: Number(reportLimitDetail.serviceCount) || 0
        }));
        api.requestTelusReport(
            props.organisationId, props.projectId, props.sketchId,
            language, requestNumber,
            props.basemap, selections, submittedLimitDetails, props.includeDeleted, props.selectedSurfaceIds,
            () => { setIsSubmitting(false); setShowSuccess(true); },
            () => setIsSubmitting(false)
        );
    };

    const onClose = () => {
        if (props.onComplete) {
            props.onComplete();
        }
    };

    return (
        <>
            {showSuccess && (
                <SuccessModal
                    success={{ message: 'Your Telus Report is being generated. You will receive an email when it is ready to download.' }}
                    onDismiss={onClose}
                />
            )}
            <Offcanvas show placement='end' scroll={false} backdrop={false}>
                <Offcanvas.Header className='border-bottom'>
                    <Offcanvas.Title>Download Telus Report</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="d-flex flex-align-start flex-column h-100 w-100">
                        <div className="w-100 h-100 mb-auto overflow-y-auto">
                            <SimpleBar autoHide={false} className='simplebar-no-autohide pe-3' style={{ maxHeight: 'calc(100vh - 200px)' }}>
                                {previousReports.length > 0 && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className='text-body fs-sm fw-semibold'>Load from Previous Report</Form.Label>
                                            <InputGroup size='sm'>
                                                <Form.Select
                                                    value={selectedReportId}
                                                    onChange={e => onSelectPreviousReport(e.target.value)}
                                                >
                                                    <option value="" hidden></option>
                                                    {previousReports.map(report => (
                                                        <option
                                                            key={report.telusReportTaskId}
                                                            value={report.telusReportTaskId}
                                                        >
                                                            {report.requestNumber || 'Untitled'} - {dateUtilities.formatToDateString(report.createdAt)} {dateUtilities.formatToTimeString(report.createdAt)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Button variant="outline-secondary" onClick={() => onSelectPreviousReport('')}>
                                                    <i className='fi-x'></i>
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </>
                                )}
                                <Form.Group className="mb-3">
                                    <Form.Label className='text-body fs-sm fw-semibold'>{language === 'fr' ? 'Langue' : 'Language'}</Form.Label>
                                    <div className="d-flex gap-4">
                                        <Form.Check
                                            type='radio'
                                            id='telus-lang-en'
                                            name='telus-language'
                                            label='English'
                                            className='text-body fs-sm'
                                            checked={language === 'en'}
                                            onChange={() => setLanguage('en')}
                                        />
                                        <Form.Check
                                            type='radio'
                                            id='telus-lang-fr'
                                            name='telus-language'
                                            label='Français'
                                            className='text-body fs-sm'
                                            checked={language === 'fr'}
                                            onChange={() => setLanguage('fr')}
                                        />
                                    </div>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className='text-body fs-sm fw-semibold'>{language === 'fr' ? 'Demande' : 'Request Number'}</Form.Label>
                                    <Form.Control
                                        size='sm'
                                        type='text'
                                        value={requestNumber}
                                        onChange={e => setRequestNumber(e.target.value)}
                                        placeholder={language === 'fr' ? 'Numéro de demande' : 'Enter request number'}
                                    />
                                </Form.Group>
                                <TelusReportLimitDetails
                                    limits={limits}
                                    language={language}
                                    selectedLimitIndices={selectedLimitIndices}
                                    onToggleLimit={toggleLimit}
                                    allLimitDetails={allLimitDetails}
                                    onChange={setAllLimitDetails}
                                />
                            </SimpleBar>
                        </div>
                        <div className="d-flex justify-content-between order-2 w-100">
                            <Button onClick={onClose} variant="secondary" size="sm">Cancel</Button>
                            <Button onClick={onSubmit} variant="primary" size="sm" disabled={isSubmitting || !canSubmit}><i className="fi-download me-2"></i> Download</Button>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}
